/**
 * WispSimulation — Race-biased MCTS rollout policy
 *
 * Extends the base Simulation with:
 * - 50% king advancement moves (race-focused)
 * - 25% capture moves (remove blockers)
 * - 25% random (exploration)
 * - WispEvaluator for non-terminal position scoring at max depth
 */

import { GameState } from "../../types";
import { Simulation } from "../MonteCarloAI/Simulation";
import { MCTSNode, MoveInfo } from "../MonteCarloAI/MCTSNode";
import { WispEvaluator } from "./WispEvaluator";

export class WispSimulation extends Simulation {
  private evaluator: WispEvaluator;

  constructor(evaluator: WispEvaluator) {
    super();
    this.evaluator = evaluator;
  }

  /**
   * Race-biased heuristic simulation.
   * Instead of generic move scoring, heavily prioritize king advancement.
   */
  simulateWithHeuristics(gameState: GameState, startingPlayer: boolean): number {
    let currentState = this.deepCopyGameState(gameState);
    let depth = 0;

    while (!this.isTerminal(currentState) && depth < this.maxSimulationDepth) {
      const moves = MCTSNode.getAllMoves(currentState);
      if (moves.length === 0) return 0.5;

      const selectedMove = this.selectRaceBiasedMove(currentState, moves);

      const nextState = this.applyMove(currentState, selectedMove);
      if (!nextState) return 0.5;
      currentState = nextState;
      depth++;
    }

    // Use WispEvaluator for non-terminal positions instead of returning 0.5
    if (!this.isTerminal(currentState)) {
      return this.evaluator.evaluate(currentState, startingPlayer);
    }

    return this.evaluateTerminal(currentState, startingPlayer);
  }

  /**
   * Race-biased move selection for rollouts.
   * 50% king advancement, 25% captures, 25% random.
   */
  private selectRaceBiasedMove(gameState: GameState, moves: MoveInfo[]): MoveInfo {
    const roll = Math.random();

    if (roll < 0.50) {
      // Try to pick a king advancement move
      const kingMove = this.findBestKingAdvance(gameState, moves);
      if (kingMove) return kingMove;
    } else if (roll < 0.75) {
      // Try to pick a capture
      const captureMove = this.findCapture(moves);
      if (captureMove) return captureMove;
    }

    // Fallback: random move
    return moves[Math.floor(Math.random() * moves.length)];
  }

  /**
   * Find the king move that advances most toward the goal row.
   * Falls back to any piece moving forward if no king move available.
   */
  private findBestKingAdvance(gameState: GameState, moves: MoveInfo[]): MoveInfo | null {
    const isLight = gameState.isLightTurn;
    let bestMove: MoveInfo | null = null;
    let bestAdvancement = -Infinity;

    for (const move of moves) {
      const piece = gameState.board[move.fromPosition.row]?.[move.fromPosition.col]
        ?.Pieces.find(p => p.id === move.move.pieceId);
      if (!piece) continue;

      const advancement = isLight
        ? move.move.position.row - move.fromPosition.row
        : move.fromPosition.row - move.move.position.row;

      if (piece.isKing && advancement > bestAdvancement) {
        bestAdvancement = advancement;
        bestMove = move;
      }
    }

    // If no king advancement, try any forward move
    if (!bestMove) {
      for (const move of moves) {
        const advancement = isLight
          ? move.move.position.row - move.fromPosition.row
          : move.fromPosition.row - move.move.position.row;

        if (advancement > bestAdvancement) {
          bestAdvancement = advancement;
          bestMove = move;
        }
      }
    }

    return bestAdvancement > 0 ? bestMove : null;
  }

  /**
   * Find a random capture move.
   */
  private findCapture(moves: MoveInfo[]): MoveInfo | null {
    const captures = moves.filter(m => m.move.toPiece);
    if (captures.length === 0) return null;
    return captures[Math.floor(Math.random() * captures.length)];
  }
}
